// src/components/FinancialDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  Target,
  Shield,
  CreditCard,
  Briefcase,
  Printer,
  User,
  Loader,
  Wallet,
  Sparkles,
  Search,
  Menu,
  Gauge,
} from 'lucide-react';

import authService from '../authentication/AuthService';
import Header from './Header';
import SidebarNav from './SidebarNav';

/* ─── dashboard tabs ──────────────────────────────────────────────────── */
import OverviewTab      from './OverviewTab';
import ProfileTab       from './ProfileTab';
import RiskAssessmentTab from './RiskAssessmentTab';
import DebtTab          from './DebtTab';

import ExpensesTab      from './ExpensesTab';
import GoalsTab         from './GoalsTab';
import IncomeTab        from './IncomeTab';
import InvestmentsTab   from './InvestmentsTab';
import NetIncomeTab     from './NetIncomeTab';
import NetWorthTab      from './NetWorthTab';
import PrintView        from './PrintView';

import DCATab from "./DCATab.js"
import RetirementTab from "./RetirementTab";
import WalletsTab from "./WalletsTab";
import ToolsTab from "./ToolsTab";
import ResearchTab from "./research/ResearchTab";
import useResearchFlags from "../hooks/useResearchFlags";

/* ─────────────────────────────────────────────────────────────────────── */

/* ─── Risk Assessment imports ─────────────────────────────────────────── */
import questions from './riskassessment/data/crypto-questions.js';
import { calculateResults } from './riskassessment/utils/cryptoCalculationUtils.js';
import QuestionSlider from './riskassessment/QuestionSlider';
import ProgressBar from './riskassessment/ProgressBar';
import CryptoReportView from './riskassessment/CryptoReportView';
import logo from '../images/arc-logo.png';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const nextMonth = (abbr) => MONTHS[(MONTHS.indexOf(abbr) + 1) % 12];

const FinancialDashboard = ({ user, onLogout }) => {
  /* ─── Research feature flags (defaults to all off until Firestore loads) ── */
  const { flags: researchFlags } = useResearchFlags();

  /* ─── UI state ──────────────────────────────────────────────────────── */
  const [activeTab,   setActiveTab]   = useState('overview');

  // Mobile sidebar drawer state. On lg+ the sidebar is permanently
  // visible regardless of this flag (CSS-controlled). On smaller
  // viewports the sidebar is hidden until the hamburger button toggles
  // this on.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ─── Deep-link: cross-tab navigation event listener ──────────────────── */
  // Any child component anywhere in the tree can navigate by dispatching:
  //   window.dispatchEvent(new CustomEvent('arc:navigate', {
  //     detail: { tab: 'research', researchFilter: { riskCharacter: 'Conservative' } }
  //   }));
  // This avoids threading a callback through five layers of props.
  const [pendingResearchFilter, setPendingResearchFilter] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const tab = e.detail?.tab;
      if (!tab) return;
      setActiveTab(tab);
      if (tab === 'research' && e.detail?.researchFilter) {
        setPendingResearchFilter(e.detail.researchFilter);
      }
    };
    window.addEventListener('arc:navigate', handler);
    return () => window.removeEventListener('arc:navigate', handler);
  }, []);
  const [editMode,    setEditMode]    = useState(false);
  const [chartsReady, setChartsReady] = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [lastSaved,   setLastSaved]   = useState(null);
  const [error,       setError]       = useState('');

  /* ─── Data state ────────────────────────────────────────────────────── */
  const [incomeData,    setIncomeData]    = useState([]);
  const [expenseData,   setExpenseData]   = useState([]);
  const [goals,         setGoals]         = useState([]);
  const [debts,         setDebts]         = useState([]);
  const [netWorth,      setNetWorth]      = useState({});
  const [emergencyFund, setEmergencyFund] = useState({});
  const [investments,   setInvestments]   = useState([]);
  const [dcaData, setDcaData] = useState({ monthlyAmount: 0, autoDcaEnabled: true, history: [] });
  const [userAge, setUserAge] = useState(30);
  const [dob, setDob] = useState("");
  const [wallets, setWallets] = useState([]);
  const [retirementSettings, setRetirementSettings] = useState({
    retireAge: 65,
    growthRate: 0.07,
    autoEstimate: false,
    timeRange: "all",
    contributionMode: "simulated",
    simulatedMonthly: "",
    drawdownYears: 30,
    drawdownReturn: 0.05,
  });


  /* ─── Column configuration state ─────────────────────────────────────── */
  const [incomeColumns, setIncomeColumns] = useState(['salary', 'business', 'rental', 'crypto', 'affiliate']);
  const [expenseColumns, setExpenseColumns] = useState(['rent', 'utilities', 'insurance', 'groceries', 'entertainment', 'travel', 'other']);

  /* ─── Investment enhancement state ───────────────────────────────────── */
  const [investmentCategories, setInvestmentCategories] = useState(['Emergency Fund', 'Retirement', 'Growth', 'Income', 'Speculative', 'Real Estate', 'Crypto']);
  const [investmentHistory, setInvestmentHistory] = useState({});

  /* ─── Risk Assessment state ──────────────────────────────────────────── */
const [riskAssessmentData, setRiskAssessmentData] = useState(null);

  /* ─── default data (for fresh accounts or load failure) ─────────────── */
  const getDefaultData = () => ({
    incomeData: [
      { month: 'Oct', salary:  5000, business: 2500, rental:  300, crypto:  300, affiliate: 1000, total:   9100 },
      { month: 'Nov', salary:  6000, business: 2500, rental:  300, crypto: 1000, affiliate: 2000, total:  11800 },
      { month: 'Dec', salary: 15000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  15000 },
      { month: 'Jan', salary: 20000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  20000 },
      { month: 'Feb', salary: 30000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  30000 },
      { month: 'Mar', salary: 40000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  40000 },
      { month: 'Apr', salary: 50000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  50000 },
      { month: 'May', salary: 60000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  60000 },
      { month: 'Jun', salary: 70000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  70000 },
      { month: 'Jul', salary: 80000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  80000 },
      { month: 'Aug', salary: 90000, business:    0, rental:    0, crypto:    0, affiliate:    0, total:  90000 },
      { month: 'Sep', salary:100000, business:    0, rental:    0, crypto:    0, affiliate:    0, total: 100000 },
    ],
    expenseData: [
      { month: 'Oct', rent: 3500, utilities: 100, insurance: 100, groceries: 100, entertainment: 200, travel: 300, other:  300, total:  4600 },
      { month: 'Nov', rent:    0, utilities:   0, insurance:   0, groceries:   0, entertainment:   0, travel:   0, other:10000, total: 10000 },
      { month: 'Dec', rent:    0, utilities:   0, insurance:   0, groceries:   0, entertainment:   0, travel:   0, other:100000,total:100000 },
    ],
    goals: [
      { name: 'FIRE Target',       target: 2000000, current: 1000000, progress: 50 },
      { name: 'Savings Target',    target:   50000, current:       0, progress:  0 },
      { name: 'Investment Target', target:  100000, current:       0, progress:  0 },
    ],
    debts: [
      { name: 'Credit Card A', original:  1100, current:  250, paidOff: 77.27, apr: 18.99, monthlyPayment: 50, totalInterestPaid: 150 },
      { name: 'Student Loan B', original: 10000, current: 5000, paidOff: 50, apr: 18.99, monthlyPayment: 50, totalInterestPaid: 150    },
      { name: 'Car Loan C',     original:     0, current:    0, paidOff:  0, apr: 18.99, monthlyPayment: 50, totalInterestPaid: 150    },
      { name: 'Mortgage D',     original:     0, current:    0, paidOff:  0, apr: 18.99, monthlyPayment: 50, totalInterestPaid: 150    },
    ],
    netWorth:      { assets: 300000, liabilities: 5250, netWorth: 294750, fireGoal: 2000000, progress: 14.74 },
    emergencyFund: { monthlyExpenses: 5500, threeMonthGoal: 16500, sixMonthGoal: 33000, current: 0, progress: 0 },
    investments: [
      { name: 'Emergency Fund',       type: 'Emergency Fund', amount: 25000 },
      { name: 'Crypto Wallet 1 Main', type: 'Crypto',         amount: 50000 },
      { name: '401k Wallet',          type: 'Retirement',     amount: 75000 },
      { name: 'Cosmos wallet',        type: 'Speculative',    amount: 25000 },
      { name: 'RON Wallet',           type: 'Speculative',    amount: 10000 },
      { name: 'Company Wallet',       type: 'Growth',         amount: 60000 },
      { name: 'SOL Wallet',           type: 'Crypto',         amount: 30000 },
      { name: 'Home',                 type: 'Real Estate',    amount: 40000 },
      { name: 'Tokenized RE',         type: 'Real Estate',    amount: 10000 },
    ],
    investmentHistory: {
      [new Date().getFullYear()]: {
        Jan: 250000, Feb: 260000, Mar: 270000, Apr: 285000, May: 295000, Jun: 310000,
        Jul: 315000, Aug: 300000, Sep: 320000, Oct: 325000, Nov: 330000, Dec: 325000
      }
    },
    riskAssessmentData: null

  });

  /* ─── Column update handlers ─────────────────────────────────────────── */
  const updateExpenseColumns = (newColumns) => {
    console.log('Parent: Updating expense columns to:', newColumns);
    
    // 1. Update column configuration
    setExpenseColumns(newColumns);
    
    // 2. Update all expense data rows to match new columns
    setExpenseData(currentExpenseData => {
      return currentExpenseData.map(row => {
        const newRow = { month: row.month };
        
        // Add all columns from newColumns, keeping existing values or defaulting to 0
        newColumns.forEach(col => {
          newRow[col] = row[col] || 0;
        });
        
        // Recalculate total
        newRow.total = newColumns.reduce((sum, col) => sum + (newRow[col] || 0), 0);
        
        return newRow;
      });
    });
  };

  const updateIncomeColumns = (newColumns) => {
    console.log('Parent: Updating income columns to:', newColumns);
    
    // 1. Update column configuration
    setIncomeColumns(newColumns);
    
    // 2. Update all income data rows to match new columns
    setIncomeData(currentIncomeData => {
      return currentIncomeData.map(row => {
        const newRow = { month: row.month };
        
        // Add all columns from newColumns, keeping existing values or defaulting to 0
        newColumns.forEach(col => {
          newRow[col] = row[col] || 0;
        });
        
        // Recalculate total
        newRow.total = newColumns.reduce((sum, col) => sum + (newRow[col] || 0), 0);
        
        return newRow;
      });
    });
  };

  /* ─── Investment enhancement handlers ────────────────────────────────── */
  const updateInvestmentCategories = (newCategories) => {
    console.log('Parent: Updating investment categories to:', newCategories);
    setInvestmentCategories(newCategories);
  };

  const updateInvestmentHistory = (year, month, value) => {
    console.log('Parent: Updating investment history:', year, month, value);
    setInvestmentHistory(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        [month]: value
      }
    }));
  };

  /* ─── add-row helpers ───────────────────────────────────────────────── */
  const addNextMonth = () => {
    if (!incomeData.length || !expenseData.length) return;

    const next = nextMonth(incomeData[incomeData.length - 1].month);

    // New income row
    const newIncomeRow = { month: next, total: 0 };
    incomeColumns.forEach(col => (newIncomeRow[col] = 0));

    // New expense row
    const newExpenseRow = { month: next, total: 0 };
    expenseColumns.forEach(col => (newExpenseRow[col] = 0));

    setIncomeData(prev => [...prev, newIncomeRow]);
    setExpenseData(prev => [...prev, newExpenseRow]);
  };


  const deleteMonth = (index) => {
    if (!window.confirm("Are you sure you want to delete this month?")) return;

    setIncomeData(prev => prev.filter((_, i) => i !== index));
    setExpenseData(prev => prev.filter((_, i) => i !== index));
  };


 const addDebt = () => {
    setEditMode(true);                                   
    setDebts((d) => [
      ...d,
      { name: 'New Debt', original: 0, current: 0, paidOff: 0, apr: 0, monthlyPayment: 0, totalInterestPaid: 0},
    ]);
  };
  
  const removeDebt = (index) => {
    if (!window.confirm("Are you sure you want to delete this debt?")) return;
    setDebts((d) => d.filter((_, i) => i !== index));
  };

  const addInvestment = (newInvestment) => {
    const investment = newInvestment || { name: 'New Investment', type: 'Emergency Fund', amount: 0 };
    setInvestments((d) => [...d, investment]);
  };

  const removeInvestment = (index) => {
    setInvestments((d) => d.filter((_, i) => i !== index));
  };

  /* ─── data loading (runs on user change) ─────────────────────────────── */
  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const res = await authService.loadFinancialData();
        if (res.success && res.data && Object.keys(res.data).length) {
          const d = res.data;
          
          // Load column configurations
          setIncomeColumns(d.incomeColumns || ['salary', 'business', 'rental', 'crypto', 'affiliate']);
          setExpenseColumns(d.expenseColumns || ['rent', 'utilities', 'insurance', 'groceries', 'entertainment', 'travel', 'other']);
          
          // Load investment configurations
          setInvestmentCategories(d.investmentCategories || ['Emergency Fund', 'Retirement', 'Growth', 'Income', 'Speculative', 'Real Estate', 'Crypto']);
          setInvestmentHistory(d.investmentHistory || {});
          
          // Load data
          setIncomeData(   d.incomeData    ?? getDefaultData().incomeData);
          setExpenseData(  d.expenseData   ?? getDefaultData().expenseData);
          setGoals(        d.goals         ?? getDefaultData().goals);
          setDebts(        d.debts         ?? getDefaultData().debts);
          setNetWorth(     d.netWorth      ?? getDefaultData().netWorth);
          setEmergencyFund(d.emergencyFund ?? getDefaultData().emergencyFund);
          setInvestments(  d.investments   ?? getDefaultData().investments);
          setRiskAssessmentData(d.riskAssessmentData ?? null);
          setWallets(d.wallets ?? []);
          if (d.dcaData) {
            setDcaData({
              monthlyAmount: 0,
              autoDcaEnabled: true,
              history: [],
              autoPercent: 20,
              ...d.dcaData,
            });
          }
          if (d.dob) setDob(d.dob);
          if (d.userAge) setUserAge(d.userAge);
          if (d.retirementSettings) {
            setRetirementSettings((prev) => ({ ...prev, ...d.retirementSettings }));
          }

          const migratedDebts = (d.debts ?? getDefaultData().debts).map(debt => ({
              ...debt,
              apr: debt.apr ?? 0,                           
              monthlyPayment: debt.monthlyPayment ?? 0,     
              totalInterestPaid: debt.totalInterestPaid ?? 0 
            }));
            
          setDebts(migratedDebts);

          if (d.updatedAt?.seconds) setLastSaved(new Date(d.updatedAt.seconds * 1000));
        } else {
          const def = getDefaultData();
          setIncomeData(def.incomeData);
          setExpenseData(def.expenseData);
          setGoals(def.goals);
          setDebts(def.debts);
          setNetWorth(def.netWorth);
          setEmergencyFund(def.emergencyFund);
          setInvestments(def.investments);
          setInvestmentHistory(def.investmentHistory);
          // Keep default columns and categories as set above
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load your financial data. Using default values.');
        const def = getDefaultData();
        setIncomeData(def.incomeData);
        setExpenseData(def.expenseData);
        setGoals(def.goals);
        setDebts(def.debts);
        setNetWorth(def.netWorth);
        setEmergencyFund(def.emergencyFund);
        setInvestments(def.investments);
        setInvestmentHistory(def.investmentHistory);
        // Keep default columns and categories as set above
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  /* ─── save to Firebase (throttled autosave) ──────────────────────────── */
  const saveUserData = async () => {
    if (!user || saving) return;
    if (!authService.isAuthenticated()) {
      setError('User authentication expired. Please log in again.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await authService.saveFinancialData({
        // Add column configurations
        incomeColumns,
        expenseColumns,
        // Add investment configurations
        investmentCategories,
        investmentHistory,
        // Existing data
        incomeData,
        expenseData,
        goals,
        debts,
        netWorth,
        emergencyFund,
        investments,
        riskAssessmentData,
        dcaData,
        userAge,
        dob,
        wallets,
        retirementSettings,
      });
      if (res.success) setLastSaved(new Date());
      else setError('Failed to save data. Changes are stored locally.');
    } catch (e) {
      console.error(e);
      setError('Failed to save your data. Changes are stored locally.');
    } finally {
      setSaving(false);
    }
  };

  /* autosave after 2 s idle */
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(saveUserData, 2000);
    return () => clearTimeout(t);
  }, [incomeColumns, expenseColumns, investmentCategories, investmentHistory, incomeData, expenseData, goals, debts, netWorth, emergencyFund, investments, riskAssessmentData, dcaData, userAge, dob, wallets, retirementSettings]);

  /* allow Recharts to mount after first paint */
  useEffect(() => {
    const t = setTimeout(() => setChartsReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  /* ─── derive & recalculate totals ------------------------------------- */
  useEffect(() => {
    if (loading) return;

    /* Clean up deleted investments */
    setInvestments(currentInvestments => 
      currentInvestments.filter(inv => !inv.name.includes('[DELETED]'))
    );

    /* income totals - use dynamic columns */
    const newIncome = incomeData.map((r) => ({
      ...r,
      total: incomeColumns.reduce((sum, col) => sum + (r[col] || 0), 0),
    }));
    if (JSON.stringify(newIncome) !== JSON.stringify(incomeData)) setIncomeData(newIncome);

    /* expense totals - use dynamic columns */
    const newExpense = expenseData.map((r) => ({
      ...r,
      total: expenseColumns.reduce((sum, col) => sum + (r[col] || 0), 0),
    }));
    if (JSON.stringify(newExpense) !== JSON.stringify(expenseData)) setExpenseData(newExpense);

    /* goal progress */
    const newGoals = goals.map((g) => ({
      ...g,
      progress: g.target ? (g.current / g.target) * 100 : 0,
    }));
    if (JSON.stringify(newGoals) !== JSON.stringify(goals)) setGoals(newGoals);

    /* debt progress */
    const newDebts = debts.map((d) => {
      const paidOffPercentage = d.original ? ((d.original - d.current) / d.original) * 100 : 0;
      
      // Calculate monthly interest if APR is provided
      const monthlyInterestRate = (d.apr || 0) / 100 / 12;
      const monthlyInterest = d.current * monthlyInterestRate;
      
      return {
          ...d,
          paidOff: paidOffPercentage,
          monthlyInterest: monthlyInterest  // This could be useful for other components
        };
    });
    
    if (JSON.stringify(newDebts) !== JSON.stringify(debts)) setDebts(newDebts);


    /* net-worth metrics */
    const debtSum = newDebts.reduce((s, d) => s + d.current, 0);
    const nw = {
      ...netWorth,
      liabilities: debtSum,
      netWorth: netWorth.assets - debtSum,
      progress: netWorth.fireGoal ? ((netWorth.assets - debtSum) / netWorth.fireGoal) * 100 : 0,
    };
    if (JSON.stringify(nw) !== JSON.stringify(netWorth)) setNetWorth(nw);

    /* emergency-fund progress */
    const ef = {
      ...emergencyFund,
      threeMonthGoal: emergencyFund.monthlyExpenses * 3,
      sixMonthGoal:   emergencyFund.monthlyExpenses * 6,
      progress: emergencyFund.monthlyExpenses
        ? (emergencyFund.current / (emergencyFund.monthlyExpenses * 6)) * 100
        : 0,
    };
    if (JSON.stringify(ef) !== JSON.stringify(emergencyFund)) setEmergencyFund(ef);
  }, [incomeData, expenseData, goals, debts, netWorth, emergencyFund, loading, incomeColumns, expenseColumns]);


  const getDebtSummary = () => {
    return debts.reduce((summary, debt) => {
      const monthlyInterestRate = (debt.apr || 0) / 100 / 12;
      const monthlyInterest = debt.current * monthlyInterestRate;
      
      return {
        totalOriginalDebt: summary.totalOriginalDebt + debt.original,
        totalCurrentDebt: summary.totalCurrentDebt + debt.current,
        totalMonthlyInterest: summary.totalMonthlyInterest + monthlyInterest,
        totalMonthlyPayments: summary.totalMonthlyPayments + (debt.monthlyPayment || 0),
        totalInterestPaid: summary.totalInterestPaid + (debt.totalInterestPaid || 0)
      };
    }, {
      totalOriginalDebt: 0,
      totalCurrentDebt: 0,
      totalMonthlyInterest: 0,
      totalMonthlyPayments: 0,
      totalInterestPaid: 0
    });
  };

  /* ─── field updaters --------------------------------------------------- */
  const updateIncomeData = (i, k, v) => {
    setIncomeData((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: parseFloat(v) || 0 };
      return c;
    });
  };
  
  // Helper for net income tab - update total income for a row
  const updateIncomeTotal = (rowIndex, totalValue) => {
    setIncomeData((currentData) => {
      const updated = [...currentData];
      if (updated[rowIndex]) {
        // Distribute the total proportionally across existing columns, or set to salary if all are zero
        const currentTotal = incomeColumns.reduce((sum, col) => sum + (updated[rowIndex][col] || 0), 0);
        
        if (currentTotal === 0) {
          // If no existing values, put everything in the first column
          const newRow = { ...updated[rowIndex] };
          incomeColumns.forEach(col => newRow[col] = 0);
          newRow[incomeColumns[0]] = parseFloat(totalValue) || 0;
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        } else {
          // Distribute proportionally
          const ratio = (parseFloat(totalValue) || 0) / currentTotal;
          const newRow = { ...updated[rowIndex] };
          incomeColumns.forEach(col => {
            newRow[col] = (newRow[col] || 0) * ratio;
          });
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        }
      }
      return updated;
    });
  };

  const updateExpenseData = (i, k, v) => {
    setExpenseData((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: parseFloat(v) || 0 };
      return c;
    });
  };
  
  // Helper for net income tab - update total expenses for a row
  const updateExpenseTotal = (rowIndex, totalValue) => {
    setExpenseData((currentData) => {
      const updated = [...currentData];
      if (updated[rowIndex]) {
        // Distribute the total proportionally across existing columns, or set to first column if all are zero
        const currentTotal = expenseColumns.reduce((sum, col) => sum + (updated[rowIndex][col] || 0), 0);
        
        if (currentTotal === 0) {
          // If no existing values, put everything in the first column
          const newRow = { ...updated[rowIndex] };
          expenseColumns.forEach(col => newRow[col] = 0);
          newRow[expenseColumns[0]] = parseFloat(totalValue) || 0;
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        } else {
          // Distribute proportionally
          const ratio = (parseFloat(totalValue) || 0) / currentTotal;
          const newRow = { ...updated[rowIndex] };
          expenseColumns.forEach(col => {
            newRow[col] = (newRow[col] || 0) * ratio;
          });
          newRow.total = parseFloat(totalValue) || 0;
          updated[rowIndex] = newRow;
        }
      }
      return updated;
    });
  };
  const updateGoal = (i, k, v) => {
    setGoals((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: k === 'name' ? v : parseFloat(v) || 0 };
      return c;
    });
  };
  const updateDebt = (i, k, v) => {
    setDebts((d) => {
      const c = [...d];
      
      // Handle string vs number fields
      if (k === 'name') {
        c[i] = { ...c[i], [k]: v };
      } else {
        // For numeric fields, ensure they're properly parsed
        const numValue = parseFloat(v) || 0;
        c[i] = { ...c[i], [k]: numValue };
        
        // Optional: Add validation for APR (typically 0-30%)
        if (k === 'apr' && numValue > 50) {
          console.warn('APR seems unusually high. Please verify.');
        }
        
        // Optional: Ensure monthly payment isn't negative
        if (k === 'monthlyPayment' && numValue < 0) {
          c[i] = { ...c[i], [k]: 0 };
        }
      }
      
      return c;
    });
  };
  const updateNetWorth = (k, v) =>
    setNetWorth((n) => ({ ...n, [k]: parseFloat(v) || 0 }));
  const updateEmergencyFund = (k, v) =>
    setEmergencyFund((e) => ({ ...e, [k]: parseFloat(v) || 0 }));
  const updateInvestment = (i, k, v) => {
    setInvestments((d) => {
      const c = [...d];
      c[i] = { ...c[i], [k]: ['name', 'type'].includes(k) ? v : parseFloat(v) || 0 };
      return c;
    });
  };

  /* ─── derived helpers -------------------------------------------------- */
  const netIncomeData = incomeData.map((inc, idx) => {
    const exp = expenseData[idx] ?? { total: 0 };
    return { month: inc.month, income: inc.total, expenses: exp.total, netIncome: inc.total - exp.total };
  });
  
  // Filter out deleted investments for calculations
  const activeInvestments = investments.filter(inv => !inv.name.includes('[DELETED]'));
  const totalInvestments = activeInvestments.reduce((s, i) => s + i.amount, 0);
  const totalDebts       = debts.reduce((s, d) => s + d.current, 0);

  /* ─── navigation tabs -------------------------------------------------- */
  // Research tab is feature-flagged off by default. Admin flips
  // researchConfig/featureFlags.researchTabEnabled in the Firebase Console
  // to expose it. Ships dark.
  const TABS = [
    { id: 'overview',    label: 'Overview',       icon: TrendingUp },
    { id: 'income',      label: 'Income',         icon: DollarSign },
    { id: 'expenses',    label: 'Expenses',       icon: CreditCard },
    { id: 'netincome',   label: 'Net Income',     icon: TrendingUp },
    { id: 'debt',        label: 'Debt',           icon: CreditCard },
    { id: 'investments', label: 'Investments',    icon: Briefcase },
    { id: 'wallets',     label: 'Wallets',        icon: Wallet },
    { id: 'dca',         label: 'DCA',            icon: Shield },
    { id: 'networth',    label: 'Net Worth',      icon: TrendingUp },
    { id: 'goals',       label: 'Goals',          icon: Target },
    { id: 'retirement',  label: 'Retirement',     icon: Briefcase },
    { id: 'tools',       label: 'Tools',          icon: Sparkles },
    { id: 'riskprofile', label: 'Risk Profile',   icon: Gauge },
    ...(researchFlags.researchTabEnabled
      ? [{ id: 'research', label: 'Research',     icon: Search }]
      : []),
    { id: 'profile',     label: 'Profile',        icon: User },
    { id: 'print',       label: 'Print',          icon: Printer },
  ];
  /* ─── tab renderer ----------------------------------------------------- */
  const renderActiveTab = (id) => {
    switch (id) {
      case 'overview':
      return (
        <OverviewTab
          saving={saving}
          lastSaved={lastSaved}
          saveUserData={saveUserData}
          error={error}
          incomeData={incomeData}
          netWorth={netWorth}
          totalDebts={totalDebts}
          totalInvestments={totalInvestments}
          emergencyFund={emergencyFund}
          netIncomeData={netIncomeData}
          goals={goals}
          investments={activeInvestments}
          debts={debts}
          expenseData={expenseData}
          wallets={wallets}
          chartsReady={chartsReady}
          debtSummary={getDebtSummary()}
        />
      );

      case 'income':
        return (
          <IncomeTab
            incomeData={incomeData}
            incomeColumns={incomeColumns}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateIncomeData={updateIncomeData}
            onUpdateColumns={updateIncomeColumns}
            addNextMonth={addNextMonth}
            deleteMonth={deleteMonth}  // 👈 shared
            setIncomeData={setIncomeData} 
            chartsReady={chartsReady}
          />
        );

      case 'expenses':
        return (
          <ExpensesTab
            expenseData={expenseData}
            expenseColumns={expenseColumns}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateExpenseData={updateExpenseData}
            addNextMonth={addNextMonth}
            deleteMonth={deleteMonth}  // 👈 shared
            setExpenseData={setExpenseData}
            onUpdateColumns={updateExpenseColumns}
          />
        );

      case 'netincome':
        return (
          <NetIncomeTab 
            netIncomeData={netIncomeData} 
            chartsReady={chartsReady}
            onUpdateIncomeData={updateIncomeTotal}
            onUpdateExpenseData={updateExpenseTotal}
            addNextMonth={addNextMonth}
            deleteMonth={deleteMonth}

          />
        );

      case 'goals':
        return (
          <GoalsTab
            goals={goals}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateGoal={updateGoal}
            investments={activeInvestments}
            totalInvestments={totalInvestments}
            totalDebts={totalDebts}
            expenseData={expenseData}
            wallets={wallets}
          />
        );

      case 'debt':
        return (
          <DebtTab
            debts={debts}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateDebt={updateDebt}
            addDebt={addDebt}
            removeDebt={removeDebt}
            chartsReady={chartsReady}
          />
        );

      case 'networth':
        return (
          <NetWorthTab
            netWorth={netWorth}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateNetWorth={updateNetWorth}
            totalInvestments={totalInvestments}
            totalDebts={totalDebts}
            wallets={wallets}
          />
        );

      case 'investments':
        return (
          <InvestmentsTab
            investments={investments}
            totalInvestments={totalInvestments}
            editMode={editMode}
            onToggleEdit={() => setEditMode(!editMode)}
            updateInvestment={updateInvestment}
            chartsReady={chartsReady}
            addInvestment={addInvestment}
            removeInvestment={removeInvestment}
            investmentCategories={investmentCategories}
            onUpdateCategories={updateInvestmentCategories}
            investmentHistory={investmentHistory}
            updateInvestmentHistory={updateInvestmentHistory}
          />
        );

      case 'wallets':
        return (
          <WalletsTab
            wallets={wallets}
            setWallets={setWallets}
          />
        );

      case 'dca':
        return (
          <DCATab
            netIncomeData={netIncomeData}
            dcaData={dcaData}
            setDcaData={setDcaData}
          />
        );

      case 'retirement':
        return (
          <RetirementTab
            userAge={userAge}
            setUserAge={setUserAge}
            dob={dob}
            setDob={setDob}
            netWorth={netWorth}
            investments={investments}
            wallets={wallets}
            netIncomeData={netIncomeData}
            dcaData={dcaData}
            investmentHistory={investmentHistory}
            retirementSettings={retirementSettings}
            setRetirementSettings={setRetirementSettings}
          />
        );

      case 'tools':
        return <ToolsTab />;

      case 'research':
        // Feature-flagged. The TAB itself is hidden from nav unless the
        // flag is on, but we guard the case here defensively in case the
        // user has a stale activeTab in their persisted state.
        return researchFlags.researchTabEnabled
          ? (
            <ResearchTab
              initialFilter={pendingResearchFilter}
              onFilterConsumed={() => setPendingResearchFilter(null)}
            />
          )
          : <ToolsTab />;

      case 'profile':
        return (
          <ProfileTab emergencyFund={emergencyFund} />
        );

      case 'riskprofile':
        return (
          <RiskAssessmentTab
            questions={questions}
            calculateResults={calculateResults}
            QuestionSlider={QuestionSlider}
            ProgressBar={ProgressBar}
            CryptoReportView={CryptoReportView}
            logo={logo}
            riskAssessmentData={riskAssessmentData}
            setRiskAssessmentData={setRiskAssessmentData}
          />
        );

      case 'print':
        return (
          <PrintView
            incomeData={incomeData}
            netIncomeData={netIncomeData}
            netWorth={netWorth}
            totalDebts={totalDebts}
            goals={goals}
            investments={activeInvestments}
            totalInvestments={totalInvestments}
          />
        );

      default:
        return null;
    }
  };

  /* ─── render ----------------------------------------------------------- */
  if (loading) {
    return (
      <div className="min-h-screen mesh flex items-center justify-center">
        <div className="text-center px-6">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="pulse-dot" aria-hidden="true" />
            <span className="eyebrow">Initializing</span>
          </div>
          <Loader className="animate-spin text-arc-gold mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="headline text-2xl mb-2">Loading your dashboard</h2>
          <p className="text-arc-muted text-sm">Fetching your financial data from the chain…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh">
      <Header user={user} onLogout={onLogout} lastUpdated={lastSaved || new Date()} />

      {/*
       * Two-column layout: SidebarNav on the left (fixed on lg+,
       * slide-in drawer on smaller screens), main content on the right.
       *
       * The sidebar replaced an earlier horizontal tab bar that
       * required scrolling and buried tabs like Research at the
       * overflow end. See SidebarNav.js for the responsive behavior.
       */}
      <div className="flex">
        <SidebarNav
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <div className="flex-1 min-w-0">
          {/* Mobile-only top bar with hamburger trigger. Hidden on lg+
              where the sidebar is permanently visible. */}
          <div className="lg:hidden sticky top-0 z-30 bg-arc-bg/80 backdrop-blur-md border-b border-arc-line px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="btn-tactile w-9 h-9 rounded-lg border border-arc-line bg-arc-surface/60 flex items-center justify-center text-arc-ink hover:border-arc-gold/40 transition-all"
              aria-label="Open navigation"
            >
              <Menu size={16} />
            </button>
            <p className="text-sm font-semibold text-arc-ink capitalize">
              {TABS.find((t) => t.id === activeTab)?.label || ""}
            </p>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            {/* body */}
            {renderActiveTab(activeTab)}
          </div>
        </div>
      </div>

      {/* print-specific CSS (inline for Cloud Print) */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              .no-print { display:none!important }
              body { print-color-adjust:exact; -webkit-print-color-adjust:exact; background:#fff!important; color:#000!important }
              .mesh { background:#fff!important }
              .glass { background:#fff!important; border-color:#e5e7eb!important; backdrop-filter:none!important }
              .print\\:shadow-none { box-shadow:none!important }
              .print\\:border { border:1px solid #e5e7eb!important }
              .print\\:space-y-4>*+* { margin-top:1rem!important }
            }
          `,
        }}
      />
    </div>
  );
};

export default FinancialDashboard;